"use client";

// Extracted from app/page.tsx.
// Owns: GeoJSON data fetch, all client-side filter logic, upload-success polling.
// Accepts userId so the "showMyProfileOnly" filter can be applied in the memo.

import { useState, useMemo, useRef, useEffect } from "react";
import { getPanoramasApiPanoramasGet, PanoramaFeatureCollection } from "@/api-client";
import { FilterState, DEFAULT_FILTERS } from "@/features/map";

export function usePanoramaData(userId: string | undefined, username: string | undefined) {
  const [data, setData]         = useState<PanoramaFeatureCollection | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters]   = useState<FilterState>(DEFAULT_FILTERS);
  const [timeCutoff, setTimeCutoff] = useState<string>("");
  const [showMyProfileOnly, setShowMyProfileOnly] = useState(false);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  async function loadPanoramas() {
    try {
      const response = await getPanoramasApiPanoramasGet();
      if (response.data) setData(response.data as PanoramaFeatureCollection);
    } catch (err) {
      console.error("Failed to fetch panoramas from API:", err);
    } finally {
      setIsLoading(false);
    }
  }

  /** Start the 5-attempt polling loop after a successful upload.
   *  Exposed so page.tsx can pass it directly to PersonalDashboard.onUploadSuccess. */
  function handleUploadSuccess() {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    let attempts = 0;
    pollIntervalRef.current = setInterval(async () => {
      const res = await getPanoramasApiPanoramasGet();
      if (res.data) setData(res.data as PanoramaFeatureCollection);
      attempts++;
      if (attempts >= 5) {
        clearInterval(pollIntervalRef.current!);
        pollIntervalRef.current = null;
      }
    }, 2000);
  }

  // Initial load + cleanup
  useEffect(() => {
    loadPanoramas();
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  // ── Client-side filtering ────────────────────────────────────────────────
  const filteredData = useMemo(() => {
    if (!data) return null;
    const filtered = data.features.filter((f) => {
      const p = f.properties as any;

      if (filters.minRating > 0 && (p.average_rating ?? 0) < filters.minRating) return false;

      if (filters.dateFrom && p.capture_date) {
        if (new Date(p.capture_date) < new Date(filters.dateFrom)) return false;
      }
      if (filters.dateTo && p.capture_date) {
        if (new Date(p.capture_date) > new Date(filters.dateTo + "T23:59:59")) return false;
      }

      if (timeCutoff && p.capture_date) {
        if (new Date(p.capture_date) > new Date(timeCutoff + "T23:59:59")) return false;
      }

      if (showMyProfileOnly && userId) {
        if (p.user_id !== userId && p.creator_name !== username) return false;
      }

      if (filters.hideHotspotTypes.length > 0 && Array.isArray(p.hotspot_types)) {
        if (filters.hideHotspotTypes.some((t: string) => p.hotspot_types.includes(t))) return false;
      }

      return true;
    });
    return { ...data, features: filtered };
  }, [data, filters, timeCutoff, showMyProfileOnly, userId, username]);

  const allCaptureDates = useMemo(
    () => (data?.features ?? []).map((f) => (f.properties as any).capture_date),
    [data]
  );

  return {
    data,
    isLoading,
    filteredData,
    allCaptureDates,
    filters,       setFilters,
    timeCutoff,    setTimeCutoff,
    showMyProfileOnly, setShowMyProfileOnly,
    loadPanoramas,
    handleUploadSuccess,
  };
}
